import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Heart, ArrowRight } from '@phosphor-icons/react';

import { bubbleImageSrc } from '@/lib/bubbleImage';
const BrushFinish = () => {
  const { token } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [peek, setPeek] = useState(null);
  const [bubbles, setBubbles] = useState([]);
  const [picked, setPicked] = useState(null);
  const [place, setPlace] = useState('');
  const [busy, setBusy] = useState(false);
  const [met, setMet] = useState(null);

  useEffect(() => {
    if (!user) {
      localStorage.setItem('bx_pending_brush', token);
      return;
    }
    api.get(`/brush/peek/${token}`)
       .then(({ data }) => setPeek(data))
       .catch((err) => toast.error(err?.response?.data?.detail || 'expired brush'));
    api.get('/bubbles/mine').then(({ data }) => setBubbles(data.bubbles || []));
  }, [token, user]);

  const finish = async () => {
    if (!picked) { toast.error('Pick a bubble first'); return; }
    setBusy(true);
    try {
      const { data } = await api.post('/brush/finish', {
        token, bubble_id: picked.id, place: place.slice(0, 60),
      });
      if (navigator.vibrate) try { navigator.vibrate([20, 30, 40, 30, 200]); } catch {}
      localStorage.removeItem('bx_pending_brush');
      setMet(data.met);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'brush failed');
    } finally { setBusy(false); }
  };

  if (!user) {
    return (
      <div className="min-h-screen grid place-items-center px-4">
        <div className="max-w-sm text-center">
          <Heart size={36} weight="fill" className="text-bx-magenta mx-auto" />
          <h1 className="font-display text-3xl font-black tracking-tighter mt-3">
            Someone wants their bubble to meet yours
          </h1>
          <p className="text-white/65 mt-3">Sign in to brush back.</p>
          <Link to="/register" data-testid="brush-finish-register"
                className="btn-primary block px-7 py-3.5 rounded-full text-sm mt-6">Claim invite</Link>
          <Link to="/login" data-testid="brush-finish-login" className="text-bx-cyan font-bold text-sm inline-block mt-3">
            Already in? Sign in →
          </Link>
        </div>
      </div>
    );
  }

  if (met) {
    return (
      <div className="min-h-screen grid place-items-center px-4 py-10" data-testid="brush-finish-met">
        <motion.div initial={{ scale: 0.7, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
                    transition={{ type: 'spring', stiffness: 140 }}
                    className="max-w-lg w-full text-center">
          <Heart size={48} weight="fill" className="text-bx-magenta mx-auto" />
          <h1 className="font-display text-5xl font-black tracking-tighter mt-4">
            They <span className="shimmer-text">met</span>
          </h1>
          <p className="text-white/65 mt-3">
            Both bubbles now carry the memory of this moment
            {met.place ? <> at <span className="text-white">{met.place}</span></> : null}.
          </p>
          <div className="my-10 flex items-center justify-center gap-6">
            <Avatar b={met.initiator_bubble} />
            <motion.div animate={{ scale: [1, 1.3, 1], rotate: [0, 12, -12, 0] }}
                        transition={{ duration: 1.4, repeat: Infinity }}>
              <Heart size={32} weight="fill" className="text-bx-magenta" />
            </motion.div>
            <Avatar b={met.responder_bubble} />
          </div>
          <div className="text-[10px] uppercase tracking-[0.4em] text-bx-cyan mb-6">
            recorded on the public ledger · Tier C · story
          </div>
          <button onClick={() => nav(`/bubble/${met.responder_bubble.id}`)} data-testid="brush-finish-open"
                  className="btn-primary px-7 py-3.5 rounded-full text-sm inline-flex items-center gap-2">
            Open my bubble <ArrowRight size={14} weight="bold" />
          </button>
        </motion.div>
      </div>
    );
  }

  if (!peek) {
    return <div className="min-h-screen grid place-items-center text-white/60">loading brush…</div>;
  }

  return (
    <div className="min-h-screen grid place-items-center px-4 py-10" data-testid="brush-finish-page">
      <div className="max-w-md w-full">
        <div className="text-center">
          <div className="text-[10px] uppercase tracking-[0.4em] text-bx-magenta">brush invitation</div>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-2">
            @{peek.initiator_username}'s <span className="shimmer-text">{peek.initiator_bubble_name}</span>
            <br/>wants to meet yours
          </h1>
          <p className="text-white/65 mt-3 text-sm">
            No ownership changes. Just a memory both bubbles carry forever.
          </p>
        </div>

        <div className="mt-8">
          <div className="text-[10px] uppercase tracking-widest text-white/65 mb-2">Pick which bubble meets theirs</div>
          {bubbles.length === 0 ? (
            <div className="text-white/60 text-sm">
              You need a bubble to brush.{' '}
              <Link to="/breathe" className="text-bx-cyan font-bold">Breathe one →</Link>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-2">
              {bubbles.map((b) => (
                <button key={b.id} data-testid={`brush-finish-pick-${b.id}`}
                        onClick={() => setPicked(b)}
                        className={`glass rounded-2xl p-2 text-center transition ${
                          picked?.id === b.id ? 'ring-2 ring-bx-magenta' : 'hover:bg-white/5'
                        }`}>
                  {bubbleImageSrc(b) ? (
                    <img src={bubbleImageSrc(b)} alt={b.name}
                         className="h-16 w-16 rounded-full object-cover mx-auto" />
                  ) : (
                    <div className="h-16 w-16 rounded-full bg-gradient-to-br from-bx-cyan via-bx-magenta to-bx-volt mx-auto breathe" />
                  )}
                  <div className="font-display font-bold text-xs mt-1.5 truncate">{b.name}</div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="mt-5">
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-white/65">Where are you (optional)</span>
            <input value={place} onChange={(e) => setPlace(e.target.value)}
                   placeholder="e.g. Brooklyn cafe, Lollapalooza, etc."
                   data-testid="brush-finish-place"
                   className="mt-1 w-full px-3 py-3 rounded-xl bg-white/5 border border-white/10 outline-none focus:border-bx-magenta text-sm" />
          </label>
        </div>

        <button onClick={finish} disabled={busy || !picked} data-testid="brush-finish-confirm"
                className="btn-primary px-7 py-3.5 rounded-full text-sm flex items-center justify-center gap-2 mt-7 w-full disabled:opacity-40">
          <Heart size={14} weight="fill" /> {busy ? 'Brushing…' : 'Brush back'}
        </button>
      </div>
    </div>
  );
};

const Avatar = ({ b }) => (
  <div className="text-center">
    {bubbleImageSrc(b) ? (
      <img src={bubbleImageSrc(b)} alt={b?.name}
           className="h-24 w-24 rounded-full object-cover ring-2 ring-bx-magenta/40 breathe" />
    ) : (
      <div className="h-24 w-24 rounded-full bg-gradient-to-br from-bx-cyan via-bx-magenta to-bx-volt breathe" />
    )}
    <div className="font-display font-bold text-sm mt-2 truncate max-w-[6rem]">{b?.name || '…'}</div>
  </div>
);

export default BrushFinish;
